<template>
  <div class="mt-16 d-flex position-relative">
    <BaseButton
      variant="naked"
      class="reset-btn-styling mr-8"
      :disabled="isReviewFromUser"
      @click="onClick"
    >
      <HeartIcon v-if="liked" :class="['filled-heart', animationClass]" />
      <HeartIcon v-else :class="['empty-heart', animationClass]" />
    </BaseButton>

    <section v-if="animate">
      <HeartIcon v-for="n in 4" :key="n" :class="['heart', `heart${n}`]" />
    </section>

    <span v-if="numberOfLikes" data-testid="num-of-likes"
      >{{ numberOfLikes }}
    </span>
    <p v-else class="mb-0">
      {{ isReviewFromUser ? '0' : $label('books.reviews.likeFirst') }}
    </p>
  </div>
</template>

<script>
import { ref } from 'vue'
import HeartIcon from '~/assets/icons/heart.svg'
import { useNuxtApp } from '#imports'

export default {
  components: {
    HeartIcon,
  },
  props: {
    review: {
      type: Object,
      required: true,
    },
    loggedInUser: {
      type: [Object, Boolean],
      default: false,
    },
    openLoginDrawer: {
      type: Function,
      required: true,
    },
    likeReview: {
      type: Function,
      required: true,
    },
    removeLike: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const review = toRef(props, 'review')
    const loggedInUser = toRef(props, 'loggedInUser')
    const liked = ref(!!review.value.reactionsFromCurrentUser.length)
    const animate = ref(false)
    const numberOfLikes = ref(0)
    const animationClass = ref('')
    const isReviewFromUser = ref(
      review.value.userUid === loggedInUser.value?.id,
    )
    const [heartReactions] = review.value.totalReactionsPerType
    const { $toast, $sentryCaptureException, $label } = useNuxtApp()

    if (heartReactions) {
      numberOfLikes.value = heartReactions.total
    }

    function animateHearts() {
      animate.value = true

      setTimeout(() => {
        animate.value = false
      }, 1000)
    }

    function onClick() {
      if (animate.value) return

      if (!props.loggedInUser) {
        return props.openLoginDrawer()
      }

      animationClass.value = 'animate'

      liked.value = !liked.value

      if (liked.value) {
        updateStatus('likeReview')
        animateHearts()
        numberOfLikes.value++
      } else {
        updateStatus('removeLike')
        numberOfLikes.value--
      }
    }

    async function updateStatus(method) {
      try {
        await props[method](props.review.reviewUid)
      } catch (error) {
        liked.value = !liked.value

        $toast.error($label('messages.errorOccuredMessage'))
        $sentryCaptureException(error)
      }
    }

    return {
      onClick,
      animate,
      liked,
      numberOfLikes,
      isReviewFromUser,
      animationClass,
    }
  },
}
</script>

<style lang="scss" scoped>
.btn {
  border-radius: 0 !important;
}

.btn:disabled {
  background-color: unset !important;
  color: unset !important;
}

.heart {
  fill: $terracotta-bright;
  stroke: $terracotta-bright;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
}

.heart1 {
  animation: heart-one 0.7s 1 ease-in-out;
}

.heart2 {
  animation: heart-two 0.8s 1 ease-in-out;
  animation-delay: 0.1s;
}

.heart3 {
  animation: heart-three 0.8s 1 ease-in-out;
  animation-delay: 0.2s;
}

.heart4 {
  animation: heart-four 0.9s 1 ease-in-out;
  animation-delay: 0.3s;
}

@keyframes heart-one {
  0% {
    width: 24px;
    opacity: 0;
  }
  10% {
    padding-left: 5px;
  }
  25% {
    opacity: 1;
    width: 40px;
  }
  100% {
    width: 16px;
    opacity: 0;
    padding-left: 5px;
    bottom: 70px;
  }
}

@keyframes heart-two {
  0% {
    width: 24px;
    opacity: 0;
  }
  10% {
    padding-left: 20px;
  }
  25% {
    opacity: 1;
    width: 34px;
  }
  100% {
    width: 16px;
    opacity: 0;
    bottom: 70px;
  }
}

@keyframes heart-three {
  0% {
    opacity: 0;
  }
  10% {
    margin-left: -10px;
  }
  25% {
    width: 24px;
    opacity: 1;
  }
  100% {
    width: 16px;
    opacity: 0;
    margin-left: -15px;
    bottom: 70px;
  }
}

@keyframes heart-four {
  0% {
    opacity: 0;
  }
  10% {
    padding-left: 10px;
  }
  25% {
    opacity: 1;
    width: 20px;
  }
  100% {
    width: 16px;
    opacity: 0;
    bottom: 70px;
  }
}

.filled-heart {
  fill: $terracotta-bright;
  stroke: $terracotta-bright;
}

.filled-heart,
.empty-heart {
  width: 22.5px;
  height: 19.69px;
  &.animate {
    animation: fill-heart 0.7s forwards ease-out;
  }
}

@keyframes fill-heart {
  0% {
    opacity: 0;
  }
  20% {
    transform: scale(1.2);
  }
  60% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
